import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Chip,
    Fab,
    Drawer,
    useMediaQuery,
    useTheme,
    Button,
    ButtonGroup
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    BookmarkBorder,
    Close,
    VolumeUp,
    Star,
    ExpandMore,
    UnfoldMore,
    UnfoldLess
} from '@mui/icons-material';

const TappedWordsWidget = ({ tappedWords, isVisible = true, onToggleVisibility = null, variant = 'floating', onWordDisplayed = null, isExpanded = false, onToggleExpand = null }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expandedWords, setExpandedWords] = useState(new Set());

    // Filter words that have translation or grammar link
    const validWords = tappedWords.filter(word => 
        (word.t && word.t.trim() !== '') || // word.translation
        (word.l && word.l.trim() !== '')
    );

    const toggleWordExpansion = (wordKey) => {
        setExpandedWords(prev => {
            const newSet = new Set(prev);
            if (newSet.has(wordKey)) {
                newSet.delete(wordKey);
            } else {
                newSet.add(wordKey);
            }
            return newSet;
        });
    };

    const expandAllWords = () => {
        const allWordKeys = validWords.map((word, index) => `${word.w}-${index}`);
        setExpandedWords(new Set(allWordKeys));
    };

    const collapseAllWords = () => {
        setExpandedWords(new Set());
    };

    const allExpanded = validWords.length > 0 && expandedWords.size === validWords.length;

    const handlePlayAudio = (word) => {
        const utterance = new SpeechSynthesisUtterance(word.w);
        utterance.rate = 0.85; // Slow down the speech rate (1.0 is normal speed, lower is slower)
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
    };

    const WidgetContent = () => (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BookmarkBorder color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                        New Words
                    </Typography>
                    <Chip 
                        label={validWords.length} 
                        size="small" 
                        color="primary"
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {validWords.length > 0 && (
                        <ButtonGroup size="small" variant="outlined">
                            <Button
                                onClick={allExpanded ? collapseAllWords : expandAllWords}
                                startIcon={allExpanded ? <UnfoldLess /> : <UnfoldMore />}
                                sx={{ textTransform: 'none' }}
                            >
                                {allExpanded ? 'Collapse All' : 'Expand All'}
                            </Button>
                        </ButtonGroup>
                    )}
                    {onToggleVisibility && (
                        <IconButton 
                            onClick={onToggleVisibility}
                            size="small"
                            aria-label="Hide tapped words"
                        >
                            {isMobile ? <Close /> : <VisibilityOff />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                p: 1
            }}>
                {validWords.length === 0 ? (
                    <Box sx={{ 
                        textAlign: 'center', 
                        py: 4,
                        color: 'text.secondary',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <BookmarkBorder sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                        <Typography variant="body2">
                            Tap on words while reading to see them here
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        p: 1.5
                    }}>
                        {validWords.map((word, index) => {
                            const wordKey = `${word.w}-${index}`;
                            const isExpanded = expandedWords.has(wordKey);
                            
                            return (
                                <Box
                                    key={wordKey}
                                    onClick={() => toggleWordExpansion(wordKey)}
                                    sx={{
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        backgroundColor: isExpanded ? 'background.paper' : 'transparent',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                            borderColor: 'primary.main'
                                        }
                                    }}
                                >
                                    {/* Collapsed: Just the word */}
                                    {!isExpanded ? (
                                        <Box sx={{ 
                                            px: 1.5, 
                                            py: 0.75,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            <Typography 
                                                variant="body2" 
                                                fontWeight={500}
                                                color="text.primary"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    lineHeight: 1.5
                                                }}
                                            >
                                                {word.w}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        /* Expanded: Word with POS and translation */
                                        <Box sx={{ p: 1.5 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {/* Word and POS */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography 
                                                            variant="body1" 
                                                            fontWeight="bold"
                                                            color="primary.main"
                                                        >
                                                            {word.w}
                                                        </Typography>
                                                        {word.p && ( 
                                                            <Typography 
                                                                variant="caption" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    backgroundColor: 'grey.100',
                                                                    px: 1,
                                                                    py: 0.25,
                                                                    borderRadius: 1,
                                                                    fontSize: '0.7rem'
                                                                }}
                                                            >
                                                                {word.p}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <IconButton 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePlayAudio(word);
                                                        }} 
                                                        size="small"
                                                        aria-label="Play pronunciation"
                                                        sx={{ 
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                color: 'primary.main'
                                                            }
                                                        }}
                                                    >
                                                        <VolumeUp fontSize="small" />
                                                    </IconButton>
                                                </Box>

                                                {/* Translation or Grammar Link */}
                                                <Box>
                                                    {word.t && word.t.trim() !== '' ? (
                                                        <Typography 
                                                            variant="body2" 
                                                            color="text.secondary"
                                                            sx={{ lineHeight: 1.4 }}
                                                        >
                                                            {word.t}
                                                        </Typography>
                                                    ) : word.l && word.lt ? (
                                                        <Typography 
                                                            variant="body2" 
                                                            color="primary.main"
                                                            component="a"
                                                            href={word.l}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            sx={{ 
                                                                textDecoration: 'none',
                                                                '&:hover': {
                                                                    textDecoration: 'underline'
                                                                }
                                                            }}
                                                        >
                                                            {word.lt}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );

    // Fixed sidebar variant for reading pages
    if (variant === 'sidebar') {
        return (
            <Paper
                elevation={2}
                sx={{
                    position: 'sticky',
                    top: 80,
                    height: 'calc(100vh - 100px)',
                    width: '100%',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <WidgetContent />
            </Paper>
        );
    }

    // If no words, don't render anything for floating variant
    if (validWords.length === 0 && variant === 'floating') {
        return null;
    }

    // Inline variant for LongPosts, ShortStories, and Videos
    if (variant === 'inline') {
        // Only render if there are valid words
        if (validWords.length === 0) {
            return null;
        }
        return (
            <Paper
                elevation={2}
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <WidgetContent />
            </Paper>
        );
    }

    // Sticky bottom bar variant for videos
    if (variant === 'sticky-bottom') {
        if (validWords.length === 0) {
            return null;
        }

        return (
            <Paper
                elevation={8}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    borderRadius: 0,
                    borderTop: '2px solid',
                    borderColor: 'primary.main',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.15)'
                }}
            >
                {/* Collapsed Bar */}
                {!isExpanded && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1.5,
                            px: 2,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'action.hover'
                            }
                        }}
                        onClick={onToggleExpand}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Star sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight="bold">
                                New words
                            </Typography>
                            <Chip 
                                label={validWords.length} 
                                size="small" 
                                color="primary"
                                sx={{ height: '24px' }}
                            />
                        </Box>
                        <IconButton 
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onToggleExpand) onToggleExpand();
                            }}
                        >
                            <ExpandMore />
                        </IconButton>
                    </Box>
                )}

                {/* Expanded Bottom Sheet */}
                {isExpanded && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '60vh' }}>
                        {/* Header */}
                        <Box sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'grey.50'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Star sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    New words
                                </Typography>
                                <Chip 
                                    label={validWords.length} 
                                    size="small" 
                                    color="primary"
                                />
                            </Box>
                            <IconButton 
                                onClick={onToggleExpand}
                                size="small"
                                aria-label="Close"
                            >
                                <Close />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'auto',
                            p: 2
                        }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 1
                            }}>
                                {validWords.map((word, index) => {
                                    const wordKey = `${word.w}-${index}`;
                                    const isWordExpanded = expandedWords.has(wordKey);
                                    
                                    return (
                                        <Box
                                            key={wordKey}
                                            onClick={() => toggleWordExpansion(wordKey)}
                                            sx={{
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                backgroundColor: isWordExpanded ? 'background.paper' : 'transparent',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    borderColor: 'primary.main'
                                                }
                                            }}
                                        >
                                            {/* Collapsed: Just the word */}
                                            {!isWordExpanded ? (
                                                <Box sx={{ 
                                                    px: 1.5, 
                                                    py: 0.75,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5
                                                }}>
                                                    <Typography 
                                                        variant="body2" 
                                                        fontWeight={500}
                                                        color="text.primary"
                                                        sx={{
                                                            fontSize: '0.875rem',
                                                            lineHeight: 1.5
                                                        }}
                                                    >
                                                        {word.w}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                /* Expanded: Word with POS and translation */
                                                <Box sx={{ p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        {/* Word and POS */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                <Typography 
                                                                    variant="body1" 
                                                                    fontWeight="bold"
                                                                    color="primary.main"
                                                                >
                                                                    {word.w}
                                                                </Typography>
                                                                {word.p && ( 
                                                                    <Typography 
                                                                        variant="caption" 
                                                                        color="text.secondary"
                                                                        sx={{ 
                                                                            backgroundColor: 'grey.100',
                                                                            px: 1,
                                                                            py: 0.25,
                                                                            borderRadius: 1,
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    >
                                                                        {word.p}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <IconButton 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePlayAudio(word);
                                                                    }} 
                                                                    size="small"
                                                                    aria-label="Play pronunciation"
                                                                    sx={{ 
                                                                        color: 'text.secondary',
                                                                        '&:hover': {
                                                                            color: 'primary.main'
                                                                        }
                                                                    }}
                                                                >
                                                                    <VolumeUp fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    size="small"
                                                                    aria-label="Mark learned"
                                                                    sx={{ 
                                                                        color: 'text.secondary',
                                                                        '&:hover': {
                                                                            color: 'primary.main'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Star fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>

                                                        {/* Translation or Grammar Link */}
                                                        <Box>
                                                            {word.t && word.t.trim() !== '' ? (
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="text.secondary"
                                                                    sx={{ lineHeight: 1.4 }}
                                                                >
                                                                    {word.t}
                                                                </Typography>
                                                            ) : word.l && word.lt ? (
                                                                <Typography 
                                                                    variant="body2" 
                                                                    color="primary.main"
                                                                    component="a"
                                                                    href={word.l}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    sx={{ 
                                                                        textDecoration: 'none',
                                                                        '&:hover': {
                                                                            textDecoration: 'underline'
                                                                        }
                                                                    }}
                                                                >
                                                                    {word.lt}
                                                                </Typography>
                                                            ) : null}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Paper>
        );
    }

    // Floating variant (original behavior)
    if (isMobile) {
        // Mobile: Drawer implementation
        return (
            <>
                {/* Fixed Toggle Button for Mobile */}
                <Fab
                    color="primary"
                    size="medium"
                    onClick={onToggleVisibility}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        zIndex: 1000,
                        boxShadow: 3
                    }}
                    aria-label="Toggle tapped words"
                >
                    <BookmarkBorder />
                </Fab>

                {/* Mobile Drawer */}
                <Drawer
                    anchor="bottom"
                    open={isVisible}
                    onClose={onToggleVisibility}
                    PaperProps={{
                        sx: {
                            height: '70vh',
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16
                        }
                    }}
                >
                    <WidgetContent />
                </Drawer>
            </>
        );
    }

    // Desktop: Side panel implementation (overlay)
    return (
        <>
            {/* Desktop Toggle Button */}
            {!isVisible && (
                <IconButton
                    onClick={onToggleVisibility}
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        right: 10,
                        zIndex: 1000,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'primary.dark'
                        }
                    }}
                    aria-label="Show tapped words"
                >
                    <Visibility />
                </IconButton>
            )}

            {/* Desktop Side Panel */}
            {isVisible && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        top: 80,
                        right: 20,
                        width: 320,
                        height: 'calc(100vh - 120px)',
                        zIndex: 1000,
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}
                >
                    <WidgetContent />
                </Paper>
            )}
        </>
    );
};

export default TappedWordsWidget; 